/**
 * Les analyses conduites sur l'affaire, et l'instantané que chacune observe.
 *
 * La colonne « Instantané » est celle qui compte : elle dit sur quel état du
 * dossier un chiffrage a été calculé. Sans elle, « refaire le métré de mars »
 * n'aurait pas de sens.
 */
import { api, type Page } from "../../api/client";
import type { Mission, Travail } from "../../api/types";
import { Chargement, Echec, Vide } from "../../composants/etats";
import { Etat } from "../../composants/etiquettes";
import { quand } from "../../composants/formats";
import { useDonnees, useSuivreLaFile, type Veille } from "../../composants/donnees";

export function Activite({
  affaireId, veille,
}: {
  affaireId: string;
  veille: Veille<Travail>;
}) {
  const missions = useDonnees<Page<Mission>>(
    () => api.lire<Page<Mission>>(`/api/v1/affaires/${affaireId}/missions`),
    [affaireId],
  );

  useSuivreLaFile(veille.empreinte, missions.recharger);

  if (missions.charge && !missions.donnees) return <Chargement lignes={3} />;
  if (missions.erreur) {
    return <Echec erreur={missions.erreur} reessayer={missions.recharger} />;
  }

  const liste = missions.donnees?.donnees ?? [];

  return (
    <div className="carte">
      {liste.length === 0 ? (
        <Vide
          titre="Aucune analyse"
          aide="Ouvrez l'assistant et posez-lui une question sur cette affaire."
        />
      ) : (
        <div className="defilant">
          <table className="tableau">
            <thead>
              <tr>
                <th>Mission</th>
                <th>État</th>
                <th>Instantané</th>
                <th>Ouverte le</th>
                <th>Modifiée le</th>
              </tr>
            </thead>
            <tbody>
              {liste.map((mission) => (
                <tr key={mission.run_id}>
                  <td className="mono">{mission.mission_id}</td>
                  <td><Etat genre="mission" valeur={mission.etat} /></td>
                  <td className="nombre">
                    {mission.generation ?? <span className="faible">—</span>}
                  </td>
                  <td className="discret nombre">{quand(mission.ouverte_le)}</td>
                  <td className="discret nombre">{quand(mission.modifiee_le)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
